/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useWBL } from '../../context/WBLContext';
import { HeroBanner } from '../Common/HeroBanner';
import { db } from '../../firebase';
import { collection, doc, onSnapshot, setDoc, query, orderBy } from 'firebase/firestore';
import { 
  BookOpen, 
  ChevronLeft, 
  ChevronRight, 
  CheckCircle, 
  Play, 
  Award, 
  RotateCcw, 
  Clock, 
  Lock, 
  Unlock, 
  Sparkles,
  ArrowLeft,
  Volume2,
  FileText,
  AlertCircle,
  TrendingUp,
  GraduationCap,
  X,
  Printer,
  Mail,
  Send,
  Search
} from 'lucide-react';
import { Foundation, Slide } from '../../types';
import { useInAppDialog } from '../Common/InAppDialog';

interface FoundationProgress {
  userId: string;
  foundationId: string;
  status: 'not_started' | 'in_progress' | 'completed';
  currentSlideIndex: number;
  completedSlides: string[];
  lastAccessed: string;
  completedAt?: string;
  quizScore?: number;
}

const PILLAR_COLORS: Record<string, { bg: string; text: string; border: string; accent: string; gradient: string }> = {
  'Communication': { 
    bg: 'bg-blue-50', 
    text: 'text-blue-700', 
    border: 'border-blue-100', 
    accent: '#00a3e0',
    gradient: 'from-blue-500 to-indigo-600'
  },
  'Problem Solving': { 
    bg: 'bg-emerald-50', 
    text: 'text-emerald-700', 
    border: 'border-emerald-100', 
    accent: '#10b981',
    gradient: 'from-emerald-500 to-teal-600'
  },
  'Leadership': { 
    bg: 'bg-purple-50', 
    text: 'text-purple-700', 
    border: 'border-purple-100', 
    accent: '#8b5cf6',
    gradient: 'from-purple-500 to-fuchsia-600'
  },
  'Collaboration': { 
    bg: 'bg-sky-50', 
    text: 'text-sky-700', 
    border: 'border-sky-100', 
    accent: '#0ea5e9',
    gradient: 'from-sky-400 to-blue-600'
  },
  'Creativity': { 
    bg: 'bg-amber-50', 
    text: 'text-amber-700', 
    border: 'border-amber-100', 
    accent: '#f59e0b',
    gradient: 'from-amber-500 to-orange-600'
  },
  'Integrity': { 
    bg: 'bg-rose-50', 
    text: 'text-rose-700', 
    border: 'border-rose-100', 
    accent: '#f43f5e',
    gradient: 'from-rose-500 to-pink-600'
  }
};

const DEFAULT_COLOR = { 
  bg: 'bg-slate-50', 
  text: 'text-slate-700', 
  border: 'border-slate-100', 
  accent: '#64748b',
  gradient: 'from-slate-500 to-slate-700'
};

export const FoundationsPage: React.FC = () => {
  const { activeUser, sendManualPortalMessage } = useWBL();
  const { showAlert } = useInAppDialog();
  const [foundations, setFoundations] = useState<Foundation[]>([]);
  const [progressMap, setProgressMap] = useState<Record<string, FoundationProgress>>({});
  const [loading, setLoading] = useState(true);

  // Active playing state
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFoundation, setActiveFoundation] = useState<Foundation | null>(null);
  
  // Search filter
  const filteredFoundations = useMemo(() => {
    if (!searchQuery.trim()) return foundations;
    const q = searchQuery.toLowerCase().trim();
    return foundations.filter(f => 
      (f.title || "").toLowerCase().includes(q) ||
      (f.description || "").toLowerCase().includes(q) ||
      (f.pillar || "").toLowerCase().includes(q)
    );
  }, [foundations, searchQuery]);

  const [currentSlideIdx, setCurrentSlideIdx] = useState<number>(0);
  const [slideTimer, setSlideTimer] = useState<number>(0);
  const [isSlideUnlocked, setIsSlideUnlocked] = useState<boolean>(true);
  
  // Quiz player state
  const [selectedQuizOption, setSelectedQuizOption] = useState<number | null>(null);
  const [isQuizSubmitted, setIsQuizSubmitted] = useState<boolean>(false);
  const [isQuizCorrect, setIsQuizCorrect] = useState<boolean | null>(null);

  // First attempt results to track mastery level accurately
  const [firstAttemptQuizResults, setFirstAttemptQuizResults] = useState<Record<string, boolean>>({});
  
  // Certificate and completion popups
  const [showCompletionPopup, setShowCompletionPopup] = useState<{
    foundation: Foundation;
    score: number;
    correctCount: number;
    totalQuizzes: number;
  } | null>(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Load modules & progress
  useEffect(() => {
    if (!activeUser) return;

    // Load foundations
    const qFoundations = query(collection(db, 'foundations'), orderBy('createdAt', 'desc'));
    const unsubFoundations = onSnapshot(qFoundations, (snap) => {
      const data = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Foundation[];
      setFoundations(data);
    });

    // Load progress for current student
    const progressRef = collection(db, 'foundation_progress');
    const unsubProgress = onSnapshot(progressRef, (snap) => {
      const mapped: Record<string, FoundationProgress> = {};
      snap.docs.forEach(doc => {
        const prog = doc.data() as FoundationProgress;
        if (prog.userId === activeUser.uid) {
          mapped[prog.foundationId] = prog;
        }
      });
      setProgressMap(mapped);
      setLoading(false);
    });

    return () => {
      unsubFoundations();
      unsubProgress();
    };
  }, [activeUser]);

  // Handle slide timer decrement
  useEffect(() => {
    if (!activeFoundation) return;
    const slide = activeFoundation.slides[currentSlideIdx];
    if (!slide) return;

    const minTime = slide.minViewTime || 0;
    const isSkippable = slide.skippable || false;

    // If slide is skippable or has no min time, it's unlocked immediately
    if (minTime === 0 || isSkippable) {
      setSlideTimer(0);
      setIsSlideUnlocked(true);
      return;
    }

    // Check if slide was already completed in this progress
    const foundationProgress = progressMap[activeFoundation.id];
    const isAlreadyCompleted = foundationProgress?.completedSlides?.includes(slide.id) || false;
    if (isAlreadyCompleted) {
      setSlideTimer(0);
      setIsSlideUnlocked(true);
      return;
    }

    setSlideTimer(minTime);
    setIsSlideUnlocked(false);

    const interval = setInterval(() => {
      setSlideTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setIsSlideUnlocked(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [activeFoundation, currentSlideIdx, progressMap]);

  // Launch a module
  const startModule = (found: Foundation) => {
    const prog = progressMap[found.id];
    setActiveFoundation(found);
    const resumeIndex = prog ? prog.currentSlideIndex || 0 : 0;
    setCurrentSlideIdx(resumeIndex >= found.slides.length ? 0 : resumeIndex);
    setSelectedQuizOption(null);
    setIsQuizSubmitted(false);
    setIsQuizCorrect(null);
    setFirstAttemptQuizResults({});
    setEmailSent(false);
    setEmailSending(false);

    // Track launch/access state in Firestore
    if (activeUser) {
      const progDocId = `${activeUser.uid}_${found.id}`;
      const defaultProg: FoundationProgress = {
        userId: activeUser.uid,
        foundationId: found.id,
        status: prog ? prog.status : 'in_progress',
        currentSlideIndex: resumeIndex,
        completedSlides: prog ? prog.completedSlides || [] : [],
        lastAccessed: new Date().toISOString()
      };
      setDoc(doc(db, 'foundation_progress', progDocId), defaultProg, { merge: true });
    }
  };

  // Close module player
  const exitPlayer = () => {
    setActiveFoundation(null);
  };

  // Change slide (saving progress as we go)
  const handleSlideChange = async (newIdx: number) => {
    if (!activeFoundation || !activeUser) return;
    
    const slide = activeFoundation.slides[currentSlideIdx];
    const progDocId = `${activeUser.uid}_${activeFoundation.id}`;
    const currentProg = progressMap[activeFoundation.id] || {
      userId: activeUser.uid,
      foundationId: activeFoundation.id,
      status: 'in_progress',
      currentSlideIndex: currentSlideIdx,
      completedSlides: [],
      lastAccessed: new Date().toISOString()
    };

    const newCompleted = [...(currentProg.completedSlides || [])];
    if (slide && !newCompleted.includes(slide.id)) {
      newCompleted.push(slide.id);
    }

    const isLastSlide = newIdx >= activeFoundation.slides.length;
    const finalStatus = isLastSlide ? 'completed' : 'in_progress';

    let quizPct = 100;
    let totalQuizzes = 0;
    let correctCount = 0;

    if (isLastSlide) {
      const quizSlides = activeFoundation.slides.filter(s => s.type === 'quiz');
      totalQuizzes = quizSlides.length;
      if (totalQuizzes > 0) {
        quizSlides.forEach(qs => {
          if (firstAttemptQuizResults[qs.id] === true) {
            correctCount++;
          }
        });
        quizPct = Math.round((correctCount / totalQuizzes) * 100);
      }
    }

    const updatedProg: FoundationProgress = {
      ...currentProg,
      currentSlideIndex: isLastSlide ? currentSlideIdx : newIdx,
      completedSlides: newCompleted,
      status: finalStatus as any,
      lastAccessed: new Date().toISOString()
    };

    if (isLastSlide) {
      updatedProg.completedAt = new Date().toISOString();
      updatedProg.quizScore = quizPct;
    }

    await setDoc(doc(db, 'foundation_progress', progDocId), updatedProg, { merge: true });

    if (isLastSlide) {
      if (quizPct >= 90) {
        setShowCompletionPopup({
          foundation: activeFoundation,
          score: quizPct,
          correctCount,
          totalQuizzes
        });
      } else {
        showAlert({
          title: "Mastery Level Not Yet Met",
          message: `You completed the module with a score of ${quizPct}%. Standard requirement is 90% or higher. Retry the quizzes in this module to achieve full mastery and unlock your Certificate!`,
          type: "warning"
        });
      }
      setActiveFoundation(null);
    } else {
      setCurrentSlideIdx(newIdx);
      setSelectedQuizOption(null);
      setIsQuizSubmitted(false);
      setIsQuizCorrect(null);
    }
  };

  // Submit Quiz Answer
  const submitQuizAnswer = () => {
    if (!activeFoundation || selectedQuizOption === null) return;
    const slide = activeFoundation.slides[currentSlideIdx];
    if (!slide || !slide.quiz) return;

    const correctIdx = slide.quiz.correctIndex ?? 0;
    const isCorrectAnswer = selectedQuizOption === correctIdx;

    setIsQuizCorrect(isCorrectAnswer);
    setIsQuizSubmitted(true);

    if (isCorrectAnswer) {
      setIsSlideUnlocked(true);
    }

    // Record first attempt correctness of this quiz slide
    if (firstAttemptQuizResults[slide.id] === undefined) {
      setFirstAttemptQuizResults(prev => ({
        ...prev,
        [slide.id]: isCorrectAnswer
      }));
    }
  };

  // Dispatch certificate email using WBL automated message system
  const handleEmailCertificate = async () => {
    if (!showCompletionPopup || !activeUser || !sendManualPortalMessage) return;
    setEmailSending(true);
    try {
      const studentName = activeUser.displayName || activeUser.email || 'Graduate Scholar';
      const foundationTitle = showCompletionPopup.foundation.title;
      const pillar = showCompletionPopup.foundation.pillar || 'General';
      const score = showCompletionPopup.score;
      const date = new Date().toLocaleDateString();

      const subject = `🏆 Certificate of Mastery: ${foundationTitle}`;
      const title = `Congratulations on your Achievement!`;
      const htmlContent = `
        <div style="font-family: Arial, sans-serif; padding: 24px; text-align: center; border: 8px double #0f172a; max-width: 600px; margin: auto; background-color: #f8fafc;">
          <h2 style="color: #0f172a; font-size: 24px; margin-bottom: 8px;">CERTIFICATE OF COMPLETION</h2>
          <p style="font-size: 14px; color: #64748b;">SC Graduate Foundations Academy</p>
          <div style="margin: 30px 0;">
            <p style="font-size: 14px; margin: 0;">This is to certify that</p>
            <p style="font-size: 20px; font-weight: bold; color: #00a3e0; margin: 8px 0;">${studentName}</p>
            <p style="font-size: 14px; margin: 0;">has successfully completed the Graduate Foundation Module</p>
            <p style="font-size: 18px; font-weight: bold; color: #0f172a; margin: 8px 0;">${foundationTitle}</p>
            <p style="font-size: 14px; margin: 0;">under the <strong>${pillar}</strong> curriculum pillar.</p>
          </div>
          <div style="border-top: 1px solid #e2e8f0; padding-top: 16px; margin-top: 24px;">
            <p style="font-size: 12px; color: #64748b; margin: 0;">Achieved Mastery Level of <strong>${score}%</strong> on ${date}</p>
            <p style="font-size: 12px; font-weight: bold; color: #0f172a; margin: 4px 0 0;">SC Graduate Academy Coordinator Signature</p>
          </div>
        </div>
      `;

      await sendManualPortalMessage(activeUser.email, subject, title, htmlContent);
      setEmailSent(true);
    } catch (error) {
      console.error('Failed to email certificate:', error);
      showAlert({
        title: "Certificate Dispatch Error",
        message: "We succeeded in completing the module, but failed to send the automated email. Please check your network connection.",
        type: "error"
      });
    } finally {
      setEmailSending(false);
    }
  };

  // Calculate Overall student progress summary
  const totalModules = foundations.length;
  const progressValues = Object.values(progressMap) as FoundationProgress[];
  const completedModulesCount = progressValues.filter(p => p.status === 'completed').length;
  const inProgressCount = progressValues.filter(p => p.status === 'in_progress').length;

  if (loading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#00a3e0]"></div>
        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Loading Foundations...</p>
      </div>
    );
  }

  // RENDER SLIDE PLAYER VIEW
  if (activeFoundation) {
    const slide = activeFoundation.slides[currentSlideIdx];
    const pillarStyle = PILLAR_COLORS[activeFoundation.pillar || ''] || DEFAULT_COLOR;
    const totalSlides = activeFoundation.slides.length;
    const progressPct = Math.round(((currentSlideIdx + 1) / totalSlides) * 100);

    if (!slide) {
      return (
        <div className="p-8 text-center space-y-4">
          <p className="text-slate-600 font-bold">This foundation module has no slides configured yet.</p>
          <button onClick={exitPlayer} className="px-5 py-2.5 bg-[#00a3e0] text-white rounded-xl font-bold text-xs uppercase">
            Exit Player
          </button>
        </div>
      );
    }

    // Prepare styles for the custom slide theme
    const customBg = slide.backgroundColor || '#ffffff';
    const customText = slide.textColor || '#0f172a';
    const headerSizeClass = slide.fontSizeHeader || 'text-3xl';
    const bodySizeClass = slide.fontSizeBody || 'text-sm';

    const parseMarkdownLineToHtml = (lineStr: string): string => {
      let html = lineStr;
      // Escape HTML entities to prevent accidental code execution
      html = html
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

      // Restore allowed <u> tag
      html = html.replace(/&lt;u&gt;/g, '<u>').replace(/&lt;\/u&gt;/g, '</u>');

      // 1. Bold: **text**
      html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      // 2. Italic: *text*
      html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
      // 3. Links: [text](url)
      html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-[#0094c8] underline hover:text-[#0077a3] font-medium cursor-pointer">$1</a>');
      
      return html;
    };

    // Parse simple Markdown lines for body rendering
    const renderContent = (txt: string) => {
      if (!txt) return null;
      return txt.split('\n').map((line, i) => {
        const trimmed = line.trim();
        if (trimmed.startsWith('###')) {
          const content = trimmed.replace(/^###\s*/, '');
          return (
            <h4 key={i} className="text-sm font-black uppercase tracking-tight text-inherit mt-4 mb-2">
              <span dangerouslySetInnerHTML={{ __html: parseMarkdownLineToHtml(content) }} />
            </h4>
          );
        }
        if (trimmed.startsWith('##')) {
          const content = trimmed.replace(/^##\s*/, '');
          return (
            <h3 key={i} className="text-base font-black uppercase tracking-tight text-inherit mt-5 mb-2.5">
              <span dangerouslySetInnerHTML={{ __html: parseMarkdownLineToHtml(content) }} />
            </h3>
          );
        }
        if (trimmed.startsWith('#')) {
          const content = trimmed.replace(/^#\s*/, '');
          return (
            <h2 key={i} className="text-lg font-black uppercase tracking-tight text-inherit mt-6 mb-3">
              <span dangerouslySetInnerHTML={{ __html: parseMarkdownLineToHtml(content) }} />
            </h2>
          );
        }
        if (trimmed.startsWith('-') || trimmed.startsWith('*')) {
          const content = trimmed.replace(/^[-*]\s*/, '');
          return (
            <ul key={i} className="list-disc pl-5 my-1 text-inherit opacity-90">
              <li>
                <span dangerouslySetInnerHTML={{ __html: parseMarkdownLineToHtml(content) }} />
              </li>
            </ul>
          );
        }
        return (
          <p key={i} className="leading-relaxed mb-3 text-inherit opacity-90">
            <span dangerouslySetInnerHTML={{ __html: parseMarkdownLineToHtml(line) }} />
          </p>
        );
      });
    };

    return (
      <div className="fixed inset-0 bg-slate-950 z-50 flex flex-col select-none">
        {/* Top Header Controls */}
        <div className="bg-slate-900 border-b border-slate-800 px-4 md:px-6 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center space-x-3.5">
            <button 
              onClick={exitPlayer}
              className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-all cursor-pointer"
              title="Exit Learning Hub"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <span className={`text-[8.5px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${pillarStyle.bg} ${pillarStyle.text} mr-2`}>
                {activeFoundation.pillar || 'General'}
              </span>
              <h2 className="text-xs font-black text-white uppercase tracking-tight inline-block">{activeFoundation.title}</h2>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="text-right hidden sm:block">
              <span className="text-[10px] font-black text-slate-400 block uppercase">Course Progress</span>
              <span className="text-xs font-mono font-bold text-[#00a3e0]">{currentSlideIdx + 1} / {totalSlides} Slides</span>
            </div>
            
            {/* Slide Lock State Banner */}
            <div className="flex items-center space-x-1 bg-slate-950 px-3 py-1.5 rounded-lg border border-slate-800">
              {isSlideUnlocked ? (
                <>
                  <Unlock className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-[9px] font-black uppercase text-emerald-400 tracking-wider">UNLOCKED</span>
                </>
              ) : (
                <>
                  <Lock className="w-3.5 h-3.5 text-amber-500" />
                  <span className="text-[9px] font-black uppercase text-amber-500 tracking-wider">LOCKED ({slideTimer}s)</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Global Progress Bar Indicator */}
        <div className="w-full bg-slate-800 h-1.5 shrink-0">
          <div 
            className="bg-[#00a3e0] h-full transition-all duration-500 ease-out" 
            style={{ width: `${progressPct}%` }}
          />
        </div>

        {/* Dynamic Themeable Canvas Stage */}
        <div 
          className="flex-1 overflow-y-auto flex items-center justify-center p-4 md:p-8 transition-colors duration-500"
          style={{ backgroundColor: customBg }}
        >
          <div 
            className={`w-full transition-all duration-300 ${
              slide.containerSize === 'extended' ? 'max-w-6xl' : 'max-w-4xl'
            }`}
            style={{ color: customText }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={slide.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                className="grid grid-cols-1 gap-8"
              >
                {/* 1. QUIZ TYPE SLIDE */}
                {slide.type === 'quiz' ? (
                  <div className="max-w-2xl mx-auto w-full bg-white text-slate-800 rounded-3xl p-6 md:p-8 shadow-xl border border-slate-100">
                    <div className="flex items-center space-x-2 text-indigo-600 mb-4">
                      <GraduationCap className="w-5 h-5" />
                      <span className="text-[10px] font-black uppercase tracking-widest">Knowledge Checkpoint</span>
                    </div>

                    <h3 className="text-lg font-black text-slate-900 mb-6 leading-snug">
                      {slide.quiz?.question || 'Quiz Question placeholder'}
                    </h3>

                    <div className="space-y-3 mb-6">
                      {(slide.quiz?.options || []).map((option, optIdx) => {
                        const isSelected = selectedQuizOption === optIdx;
                        const isCorrectAnswer = optIdx === (slide.quiz?.correctIndex ?? 0);
                        
                        let optionStyle = 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 hover:border-slate-300';
                        if (isQuizSubmitted) {
                          if (isCorrectAnswer) {
                            optionStyle = 'border-emerald-500 bg-emerald-50 text-emerald-800';
                          } else if (isSelected) {
                            optionStyle = 'border-rose-500 bg-rose-50 text-rose-800';
                          } else {
                            optionStyle = 'border-slate-200 bg-slate-50 text-slate-400 opacity-60';
                          }
                        } else if (isSelected) {
                          optionStyle = 'border-[#00a3e0] bg-sky-50 text-[#00a3e0] font-bold shadow-sm';
                        }

                        return (
                          <button
                            key={optIdx}
                            disabled={isQuizSubmitted}
                            onClick={() => setSelectedQuizOption(optIdx)}
                            className={`w-full p-4 rounded-xl border text-left text-xs font-bold transition-all flex items-center justify-between ${optionStyle}`}
                          >
                            <span className="flex items-center gap-3">
                              <span className="w-5 h-5 rounded-md flex items-center justify-center bg-white/80 border text-[10px] font-black">
                                {String.fromCharCode(65 + optIdx)}
                              </span>
                              <span>{option}</span>
                            </span>
                            
                            {isQuizSubmitted && isCorrectAnswer && (
                              <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                            )}
                            {isQuizSubmitted && isSelected && !isCorrectAnswer && (
                              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {/* Submit Option Controls */}
                    {!isQuizSubmitted ? (
                      <button
                        onClick={submitQuizAnswer}
                        disabled={selectedQuizOption === null}
                        className={`w-full py-3 rounded-xl font-bold text-xs uppercase tracking-widest transition-all ${
                          selectedQuizOption === null
                            ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                            : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-md'
                        }`}
                      >
                        Submit Answer
                      </button>
                    ) : (
                      <div className="animate-in fade-in duration-300">
                        {isQuizCorrect ? (
                          <div className="bg-emerald-50 border border-emerald-100 text-emerald-800 rounded-xl p-4 text-center space-y-1">
                            <p className="text-xs font-black uppercase">Excellent! Correct Answer</p>
                            <p className="text-[10px] opacity-90">Your knowledge is aligned. You may proceed to the next slide.</p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <div className="bg-rose-50 border border-rose-100 text-rose-800 rounded-xl p-4 text-center space-y-1">
                              <p className="text-xs font-black uppercase">Incorrect Answer</p>
                              <p className="text-[10px] opacity-90">Please try again to unlock the module path.</p>
                            </div>
                            <button
                              onClick={() => {
                                setSelectedQuizOption(null);
                                setIsQuizSubmitted(false);
                                setIsQuizCorrect(null);
                              }}
                              className="w-full py-2.5 border border-slate-200 text-slate-600 hover:bg-slate-50 font-bold text-xs rounded-xl flex items-center justify-center gap-1.5"
                            >
                              <RotateCcw className="w-3.5 h-3.5" /> Retry Question
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                 ) : (
                  /* 2. REGULAR CONTENT LAYOUT */
                  <div className="w-full relative rounded-3xl overflow-hidden min-h-[450px] flex flex-col justify-center border border-slate-200/40 shadow-md p-6 md:p-10"
                       style={{ 
                         backgroundColor: slide.backgroundColor || '#ffffff', 
                         color: slide.textColor || '#0f172a' 
                       }}>
                    
                    {/* Background media for 'full' and 'split' layouts */}
                    {(slide.layout === 'full' || slide.layout === 'split') && slide.assetType !== 'none' && slide.assetUrl && (
                      <div className={`absolute inset-0 w-full h-full bg-black z-0 ${slide.assetType === 'video' ? 'pointer-events-auto' : 'pointer-events-none'}`}>
                        {slide.assetType === 'video' ? (
                          <video 
                            src={slide.assetUrl} 
                            controls
                            autoPlay 
                            playsInline 
                            className="w-full h-full object-contain" 
                          />
                        ) : (
                          <img 
                            src={slide.assetUrl} 
                            alt="Visual backdrop" 
                            className="w-full h-full object-cover opacity-60" 
                            referrerPolicy="no-referrer"
                          />
                        )}
                      </div>
                    )}

                    {/* Content Body Container */}
                    <div className={`relative z-10 w-full h-full flex flex-col justify-center ${
                      slide.assetType !== 'none' && (slide.layout === 'full' || slide.layout === 'split') ? 'text-white' : ''
                    }`}>
                      
                      {/* FULL LAYOUT */}
                      {slide.layout === 'full' && (
                        <div className={`space-y-5 max-w-3xl mx-auto w-full text-${slide.alignX || 'left'} ${
                          slide.alignX === 'center' ? 'items-center text-center' : slide.alignX === 'right' ? 'items-end text-right' : 'items-start text-left'
                        }`}>
                          {slide.assetType === 'none' && (
                            <div className="w-12 h-1 bg-[#00a3e0] rounded-full mb-1"></div>
                          )}
                          {slide.assetType !== 'video' && (
                            <>
                              <h2 className={`${headerSizeClass} font-black uppercase tracking-tight`}>
                                {slide.title || 'Slide Title'}
                              </h2>
                              <div className={`markdown-body ${bodySizeClass} font-medium leading-relaxed`}>
                                {renderContent(slide.content || '')}
                              </div>
                            </>
                          )}
                        </div>
                      )}

                      {/* SPLIT LAYOUT */}
                      {slide.layout === 'split' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center w-full text-left">
                          <div className="space-y-4">
                            <h2 className={`${headerSizeClass} font-black uppercase tracking-tight`}>
                              {slide.title || 'Slide Title'}
                            </h2>
                            <div className={`markdown-body ${bodySizeClass} font-medium leading-relaxed`}>
                              {renderContent(slide.content || '')}
                            </div>
                          </div>
                          
                          <div className={`p-6 border rounded-2xl space-y-3 ${
                            slide.assetType !== 'none' 
                              ? 'bg-black/55 border-white/20 text-white shadow-xl' 
                              : 'bg-slate-50 border-slate-200 text-slate-800 shadow-sm'
                          }`}>
                            <span className="text-[10px] text-sky-400 font-extrabold uppercase tracking-wider flex items-center gap-1.5">
                              <Sparkles className="w-3.5 h-3.5" /> Additional Context Node
                            </span>
                            <div 
                              className="text-xs opacity-95 leading-relaxed font-semibold italic"
                              dangerouslySetInnerHTML={{ __html: parseMarkdownLineToHtml(slide.secondaryContent || 'Add supplementary facts or work-based learning checkpoints in the right-pane content editor.') }}
                            />
                          </div>
                        </div>
                      )}

                      {/* MEDIA LEFT LAYOUT */}
                      {slide.layout === 'media-left' && (
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center w-full">
                          <div className="md:col-span-5 bg-black/10 rounded-2xl aspect-video md:aspect-[4/3] flex items-center justify-center overflow-hidden relative border border-slate-200/40 shadow-sm">
                            {slide.assetType !== 'none' && slide.assetUrl ? (
                              slide.assetType === 'video' ? (
                                slide.assetUrl.includes('youtube.com') || slide.assetUrl.includes('youtu.be') ? (
                                  <iframe 
                                    src={slide.assetUrl.replace('watch?v=', 'embed/')}
                                    className="w-full h-full rounded-xl border-0"
                                    allowFullScreen
                                  />
                                ) : (
                                  <video 
                                    src={slide.assetUrl} 
                                    controls 
                                    className="w-full h-full object-contain rounded-xl" 
                                  />
                                )
                              ) : (
                                <img 
                                  src={slide.assetUrl} 
                                  alt="Visual cover" 
                                  className="w-full h-full object-cover rounded-xl" 
                                  referrerPolicy="no-referrer"
                                />
                              )
                            ) : (
                              <div className="text-center p-4">
                                <Sparkles className="w-8 h-8 text-slate-300 mx-auto mb-1.5" />
                                <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Media Placeholder</span>
                              </div>
                            )}
                          </div>
                          
                          <div className="md:col-span-7 space-y-4 text-left">
                            <h2 className={`${headerSizeClass} font-black uppercase tracking-tight`}>
                              {slide.title || 'Slide Title'}
                            </h2>
                            <div className={`markdown-body ${bodySizeClass} font-medium leading-relaxed`}>
                              {renderContent(slide.content || '')}
                            </div>
                            {slide.secondaryContent && (
                              <div 
                                className="mt-4 pt-4 border-t border-slate-200/30 text-xs opacity-80 leading-relaxed italic"
                                dangerouslySetInnerHTML={{ __html: parseMarkdownLineToHtml(slide.secondaryContent) }}
                              />
                            )}
                          </div>
                        </div>
                      )}

                      {/* MEDIA RIGHT LAYOUT */}
                      {slide.layout === 'media-right' && (
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center w-full">
                          <div className="md:col-span-7 space-y-4 text-left">
                            <h2 className={`${headerSizeClass} font-black uppercase tracking-tight`}>
                              {slide.title || 'Slide Title'}
                            </h2>
                            <div className={`markdown-body ${bodySizeClass} font-medium leading-relaxed`}>
                              {renderContent(slide.content || '')}
                            </div>
                            {slide.secondaryContent && (
                              <div 
                                className="mt-4 pt-4 border-t border-slate-200/30 text-xs opacity-80 leading-relaxed italic"
                                dangerouslySetInnerHTML={{ __html: parseMarkdownLineToHtml(slide.secondaryContent) }}
                              />
                            )}
                          </div>

                          <div className="md:col-span-5 bg-black/10 rounded-2xl aspect-video md:aspect-[4/3] flex items-center justify-center overflow-hidden relative border border-slate-200/40 shadow-sm">
                            {slide.assetType !== 'none' && slide.assetUrl ? (
                              slide.assetType === 'video' ? (
                                slide.assetUrl.includes('youtube.com') || slide.assetUrl.includes('youtu.be') ? (
                                  <iframe 
                                    src={slide.assetUrl.replace('watch?v=', 'embed/')}
                                    className="w-full h-full rounded-xl border-0"
                                    allowFullScreen
                                  />
                                ) : (
                                  <video 
                                    src={slide.assetUrl} 
                                    controls 
                                    className="w-full h-full object-contain rounded-xl" 
                                  />
                                )
                              ) : (
                                <img 
                                  src={slide.assetUrl} 
                                  alt="Visual cover" 
                                  className="w-full h-full object-cover rounded-xl" 
                                  referrerPolicy="no-referrer"
                                />
                              )
                            ) : (
                              <div className="text-center p-4">
                                <Sparkles className="w-8 h-8 text-slate-300 mx-auto mb-1.5" />
                                <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Media Placeholder</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Navigation bottom control bar */}
        <div className="bg-slate-900 border-t border-slate-800 px-4 md:px-6 py-4 flex items-center justify-between shrink-0">
          <button
            disabled={currentSlideIdx === 0}
            onClick={() => handleSlideChange(currentSlideIdx - 1)}
            className={`px-4 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider flex items-center gap-1.5 transition-all ${
              currentSlideIdx === 0
                ? 'text-slate-600 cursor-not-allowed opacity-50'
                : 'text-white hover:bg-slate-800 hover:text-[#00a3e0] cursor-pointer'
            }`}
          >
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>

          {/* Action indicator message */}
          {!isSlideUnlocked && (
            <p className="text-[9px] font-black uppercase text-amber-400 tracking-widest animate-pulse flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Please review for {slideTimer}s to unlock path
            </p>
          )}

          {currentSlideIdx === totalSlides - 1 ? (
            <button
              disabled={!isSlideUnlocked}
              onClick={() => handleSlideChange(totalSlides)}
              className={`px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider flex items-center gap-1.5 transition-all shadow-md ${
                !isSlideUnlocked
                  ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                  : 'bg-emerald-600 text-white hover:bg-emerald-500 hover:scale-[1.02] cursor-pointer'
              }`}
            >
              Complete Module <CheckCircle className="w-4 h-4" />
            </button>
          ) : (
            <button
              disabled={!isSlideUnlocked}
              onClick={() => handleSlideChange(currentSlideIdx + 1)}
              className={`px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider flex items-center gap-1.5 transition-all shadow-md ${
                !isSlideUnlocked
                  ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                  : 'bg-[#00a3e0] text-white hover:bg-sky-500 hover:scale-[1.02] cursor-pointer'
              }`}
            >
              Next Slide <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // MAIN FOUNDATIONS CATALOG VIEW
  return (
    <div className="w-full space-y-6 pb-12" id="foundations-page-container">
      <HeroBanner 
        title="Foundations"
        subtitle="Expand your soft skills and employability potential. Review curriculum pillars designed to prepare you for actual work-based learning placements and professional excellence."
        pageKey="foundations"
      />

      <div className="w-[95%] mx-auto space-y-8 animate-in fade-in duration-300">
        
        {/* Search bar */}
        <div className="bg-white rounded-2xl border border-slate-200/70 p-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex-1 min-w-0 relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search curriculum modules (title, description, pillar)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-50/50 border border-slate-200 outline-none rounded-xl py-2 pl-9 pr-4 focus:ring-1 focus:ring-custom-primary font-medium"
            />
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50/50 border border-blue-100/50 rounded-xl">
            <span className="text-[10px] font-black text-[#0094c8] uppercase tracking-widest font-mono">
              {filteredFoundations.length} Modules
            </span>
          </div>
        </div>

      {/* Foundations Modules Listing */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="font-extrabold text-sm text-[#0f2d4a] tracking-tight uppercase flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#00a3e0]" /> Available Modules Catalog
          </h3>
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{filteredFoundations.length} total modules available</span>
        </div>

        {filteredFoundations.length === 0 ? (
          <div className="bg-white border border-slate-100 rounded-2xl p-8 text-center max-w-md mx-auto space-y-3">
            <BookOpen className="w-10 h-10 text-slate-300 mx-auto" />
            <h4 className="text-sm font-bold text-slate-800 uppercase tracking-tight">
              {searchQuery ? 'No Matching Modules' : 'No Modules Found'}
            </h4>
            <p className="text-[11px] text-slate-500 leading-normal">
              {searchQuery 
                ? "We couldn't find any modules matching your search. Try adjusting your query."
                : "Your coordinators haven't published any soft-skill foundation modules for this tenant yet. Please check back later."}
            </p>
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="text-[#00a3e0] text-[10px] font-black uppercase tracking-widest hover:underline"
              >
                Clear Search
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredFoundations.map((found) => {
              const prog = progressMap[found.id];
              const isCompleted = prog?.status === 'completed';
              const isInProgress = prog?.status === 'in_progress';
              const pillarStyle = PILLAR_COLORS[found.pillar || ''] || DEFAULT_COLOR;

              return (
                <div 
                  key={found.id}
                  className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col group h-[400px]"
                >
                  {/* Card Cover Header */}
                  <div className="relative h-44 bg-slate-100 overflow-hidden shrink-0">
                    <img 
                      src={found.heroImage || 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&w=600&q=80'} 
                      alt={found.title} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      referrerPolicy="no-referrer"
                    />
                    
                    {/* Pillar Badge */}
                    <span className={`absolute top-3 left-3 text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg shadow-sm border ${pillarStyle.bg} ${pillarStyle.text} ${pillarStyle.border}`}>
                      {found.pillar || 'General'}
                    </span>

                    {/* Progress Status Badge */}
                    {isCompleted ? (
                      prog.quizScore !== undefined && prog.quizScore < 90 ? (
                        <span className="absolute top-3 right-3 bg-amber-500 text-white text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg flex items-center gap-1 shadow-sm">
                          Completed ({prog.quizScore}% score)
                        </span>
                      ) : (
                        <span className="absolute top-3 right-3 bg-emerald-500 text-white text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg flex items-center gap-1 shadow-sm">
                          <CheckCircle className="w-3 h-3" /> Mastered ({prog.quizScore ?? 100}%)
                        </span>
                      )
                    ) : isInProgress ? (
                      <span className="absolute top-3 right-3 bg-[#00a3e0] text-white text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg flex items-center gap-1 shadow-sm animate-pulse">
                        <TrendingUp className="w-3 h-3" /> Resume
                      </span>
                    ) : (
                      <span className="absolute top-3 right-3 bg-slate-800/80 backdrop-blur-md text-slate-200 text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg shadow-sm">
                        Not Started
                      </span>
                    )}

                    {/* Completion Date Overlay if Completed */}
                    {isCompleted && prog.completedAt && (
                      <div className="absolute bottom-3 left-3 right-3 bg-black/60 backdrop-blur-sm rounded-lg p-1.5 text-center">
                        <span className="text-[8px] font-bold text-white uppercase block">
                          Completed on {new Date(prog.completedAt).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Details */}
                  <div className="p-5 flex-1 flex flex-col justify-between">
                    <div className="space-y-2">
                      <h4 className="font-extrabold text-[#0f2d4a] text-sm uppercase tracking-tight line-clamp-1 group-hover:text-[#00a3e0] transition-colors">
                        {found.title}
                      </h4>
                      <p className="text-[11px] text-slate-500 leading-normal line-clamp-3">
                        {found.description || 'No descriptive guide has been detailed for this soft-skill graduate foundation module yet.'}
                      </p>
                    </div>

                    <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                      <div className="flex items-center space-x-1.5 text-slate-400">
                        <FileText className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-[10px] font-black font-mono">{found.slides?.length || 0} Slides</span>
                      </div>

                      <div className="flex items-center space-x-2">
                        {isCompleted && (prog.quizScore === undefined || prog.quizScore >= 90) && (
                          <button
                            onClick={() => {
                              const quizSlides = found.slides.filter(s => s.type === 'quiz');
                              setShowCompletionPopup({
                                foundation: found,
                                score: prog.quizScore ?? 100,
                                correctCount: quizSlides.length,
                                totalQuizzes: quizSlides.length
                              });
                            }}
                            className="px-3 py-2 bg-gradient-to-r from-amber-500 to-yellow-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all hover:scale-[1.02] flex items-center gap-1 shadow-sm"
                            title="View your completion certificate"
                          >
                            <Award className="w-3.5 h-3.5" /> Certificate
                          </button>
                        )}

                        <button
                          onClick={() => startModule(found)}
                          className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-1 shadow-sm cursor-pointer ${
                            isCompleted
                              ? 'bg-slate-100 text-slate-700 hover:bg-[#00a3e0] hover:text-white border border-slate-200'
                              : isInProgress
                              ? 'bg-[#00a3e0] text-white hover:bg-sky-500'
                              : 'bg-slate-900 hover:bg-[#00a3e0] text-white'
                          }`}
                        >
                          {isCompleted ? (
                            <>
                              Review Module <RotateCcw className="w-3 h-3" />
                            </>
                          ) : isInProgress ? (
                            <>
                              Resume Learning <Play className="w-3 h-3" />
                            </>
                          ) : (
                            <>
                              Start Learning <Play className="w-3 h-3" />
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 1. COMPLETION AND CERTIFICATE DIALOG MODAL */}
      {showCompletionPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/75 backdrop-blur-sm p-4 overflow-y-auto">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -15 }}
            className="bg-white rounded-3xl max-w-4xl w-full shadow-2xl overflow-hidden border border-slate-100 flex flex-col max-h-[90vh]"
          >
            {/* Modal Header */}
            <div className="bg-slate-900 px-6 py-4 flex items-center justify-between text-white border-b border-slate-800 shrink-0">
              <div className="flex items-center space-x-2.5">
                <Award className="w-5 h-5 text-amber-400" />
                <span className="text-xs font-black uppercase tracking-wider text-amber-400">Mastery Certificate Earned</span>
              </div>
              <button 
                onClick={() => {
                  setShowCompletionPopup(null);
                  setEmailSent(false);
                }}
                className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors cursor-pointer animate-in duration-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body with Scroll support */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Congratulations Message */}
              <div className="text-center space-y-2 bg-gradient-to-br from-amber-500/10 to-yellow-500/5 rounded-2xl p-6 border border-amber-200/40">
                <div className="w-12 h-12 rounded-full bg-amber-500/15 flex items-center justify-center mx-auto text-amber-600">
                  <Sparkles className="w-6 h-6 animate-pulse" />
                </div>
                <h3 className="text-xl font-extrabold text-[#0f2d4a] uppercase tracking-tight">Congratulations, Graduate!</h3>
                <p className="text-xs text-slate-600 max-w-xl mx-auto font-medium">
                  You successfully mastered <strong className="text-[#00a3e0]">{showCompletionPopup.foundation.title}</strong> at a <strong className="text-emerald-600">{showCompletionPopup.score}% mastery level</strong>! This academic milestone reflects your outstanding dedication.
                </p>
              </div>

              {/* Certificate Preview Card */}
              <div className="border border-slate-200 rounded-2xl p-4 md:p-8 bg-slate-50 relative overflow-hidden flex items-center justify-center">
                {/* Visual Certificate Container */}
                <div className="border-8 border-double border-slate-800 bg-white p-6 md:p-10 w-full max-w-2xl text-center relative aspect-[1.414/1] flex flex-col justify-between shadow-md select-text">
                  <div className="absolute inset-2 border border-slate-200 pointer-events-none"></div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-center">
                      <Award className="w-10 h-10 text-amber-500" />
                    </div>
                    <h2 className="text-xl md:text-2xl font-serif font-black uppercase tracking-wider text-slate-800">Certificate of Completion</h2>
                    <p className="text-[8px] md:text-[9px] uppercase tracking-widest text-slate-400 font-sans font-black">SC Graduate Foundations Academy</p>
                  </div>

                  <div className="my-4 space-y-3">
                    <p className="text-[10px] md:text-xs italic text-slate-500 font-serif">This is proudly presented to</p>
                    <h3 className="text-lg md:text-xl font-extrabold text-[#00a3e0] uppercase border-b border-slate-200 pb-1 max-w-md mx-auto">{activeUser?.displayName || activeUser?.email || 'Graduate Scholar'}</h3>
                    <p className="text-[10px] md:text-xs italic text-slate-500 font-serif">for successfully completing the core curriculum requirements of the module</p>
                    <h4 className="text-sm md:text-base font-black text-slate-800 uppercase tracking-tight">{showCompletionPopup.foundation.title}</h4>
                    <p className="text-[9px] font-sans text-slate-400 font-bold uppercase tracking-wider">under the "{showCompletionPopup.foundation.pillar}" learning pillar</p>
                  </div>

                  <div className="flex justify-between items-end text-left text-[8px] md:text-[9px] font-sans pt-2">
                    <div>
                      <p className="font-bold text-slate-400 uppercase tracking-wider">Date Achieved</p>
                      <p className="font-extrabold text-slate-800">{new Date().toLocaleDateString()}</p>
                    </div>
                    <div className="text-center px-2 py-1 bg-slate-50 border border-slate-100 rounded">
                      <p className="font-bold text-slate-400 uppercase tracking-wider">Mastery</p>
                      <p className="font-black text-emerald-600">{showCompletionPopup.score}%</p>
                    </div>
                    <div className="text-right">
                      <div className="w-24 border-b border-slate-300 mb-0.5"></div>
                      <p className="font-bold text-slate-400 uppercase tracking-wider">SC Placement Director</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons Footer */}
            <div className="bg-slate-50 border-t border-slate-100 px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4 shrink-0">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                Print, download or dispatch the certificate via email
              </p>
              
              <div className="flex items-center space-x-3 w-full sm:w-auto">
                {/* Print button */}
                <button
                  onClick={() => window.print()}
                  className="flex-1 sm:flex-initial px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all shadow-sm cursor-pointer"
                >
                  <Printer className="w-4 h-4" /> Print Certificate
                </button>

                {/* Email button */}
                <button
                  disabled={emailSending}
                  onClick={handleEmailCertificate}
                  className={`flex-1 sm:flex-initial px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all shadow-sm cursor-pointer ${
                    emailSent 
                      ? 'bg-emerald-600 text-white hover:bg-emerald-500' 
                      : 'bg-[#00a3e0] hover:bg-sky-500 text-white'
                  }`}
                >
                  {emailSending ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Emailing...
                    </>
                  ) : emailSent ? (
                    <>
                      <CheckCircle className="w-4 h-4" /> Emailed!
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" /> Send Email
                    </>
                  )}
                </button>

                <button
                  onClick={() => {
                    setShowCompletionPopup(null);
                    setEmailSent(false);
                  }}
                  className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-xl text-xs font-bold uppercase tracking-wider transition-all cursor-pointer"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* 2. REAL PRINTABLE HIDDEN AREA FOR WEB PRINT DIALOG TRIGGER */}
      {showCompletionPopup && (
        <div id="certificate-print-area-wrapper" className="hidden">
          <div id="certificate-print-area" className="flex flex-col justify-between items-center text-center font-serif bg-white text-slate-900 border-[16px] border-double border-slate-800 p-16 w-[1000px] h-[700px] mx-auto my-auto relative shadow-2xl">
            {/* Background filigree / border decoration */}
            <div className="absolute inset-4 border-2 border-slate-300 pointer-events-none"></div>
            
            <div className="space-y-4 relative z-10 w-full">
              <div className="flex justify-center mb-2">
                <Award className="w-16 h-16 text-amber-500" />
              </div>
              <h1 className="text-4xl font-black uppercase tracking-widest text-slate-800">Certificate of Completion</h1>
              <p className="text-xs uppercase tracking-widest text-slate-500 font-sans font-bold">SC Graduate foundations academy</p>
            </div>

            <div className="space-y-6 relative z-10 my-8 w-full">
              <p className="text-sm italic font-sans text-slate-500">This is proudly presented to</p>
              <h2 className="text-3xl font-black text-[#00a3e0] uppercase border-b-2 border-slate-300 pb-2 max-w-xl mx-auto font-sans">
                {activeUser?.displayName || activeUser?.email || 'Graduate Scholar'}
              </h2>
              <p className="text-sm italic font-sans text-slate-500">for successfully completing the core curriculum requirements of the module</p>
              <h3 className="text-2xl font-black text-slate-800 uppercase tracking-tight font-sans">
                {showCompletionPopup.foundation.title}
              </h3>
              <p className="text-xs font-sans text-slate-400 font-bold uppercase tracking-wider">under the "{showCompletionPopup.foundation.pillar}" academic learning pillar</p>
            </div>

            <div className="w-full flex justify-between items-end relative z-10 mt-6 font-sans">
              <div className="text-left">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Date of Achievement</p>
                <p className="text-xs font-extrabold text-slate-800">{new Date().toLocaleDateString()}</p>
              </div>
              <div className="text-center bg-slate-50 px-4 py-2 rounded-xl border border-slate-100">
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Mastery Grade</p>
                <p className="text-sm font-black text-emerald-600">{showCompletionPopup.score}% Mastery Level</p>
              </div>
              <div className="text-right">
                <div className="w-40 border-b border-slate-400 mb-1"></div>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Academic Academy Director</p>
                <p className="text-xs font-extrabold text-slate-800">SC Placement Board</p>
              </div>
            </div>
          </div>
        </div>
      )}

      </div>

      {/* 3. DYNAMIC STYLE SHEET FOR PRINT ACTION */}
      <style>{`
        @media print {
          html, body, #root, #root > *:not(#certificate-print-area-wrapper) {
            visibility: hidden !important;
            height: 0 !important;
            overflow: hidden !important;
          }
          #certificate-print-area-wrapper, #certificate-print-area-wrapper * {
            visibility: visible !important;
          }
          #certificate-print-area-wrapper {
            display: flex !important;
            position: fixed !important;
            left: 0 !important;
            top: 0 !important;
            width: 100vw !important;
            height: 100vh !important;
            background: white !important;
            z-index: 9999999 !important;
            margin: 0 !important;
            padding: 2rem !important;
            box-sizing: border-box !important;
            justify-content: center !important;
            align-items: center !important;
          }
          #certificate-print-area {
            width: 100% !important;
            height: 100% !important;
            max-width: 1000px !important;
            max-height: 700px !important;
            box-sizing: border-box !important;
            border: 12px double #1e293b !important;
            background: white !important;
            color: black !important;
          }
        }
      `}</style>

    </div>
  );
};
